import {useEffect, useState} from 'react';
import {createAnalyzer, getAnalyzers} from '../../../../api/EcaServices';
import {AdjustAnalyzersContent} from './AdjustAnalyzersContent';
import CustomIndexstep3LeftRail from './CustomIndexstep3LeftRail';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  setIndexAnalyzers,
  setSelectedIndexAnalyzer,
} from '../../../../redux/slices/indexingSlice';
import {t} from 'i18next';
import AurPopup from '../../../common/aur-popup/AurPopup';
import AurButton from '../../../common/aur-button/AurButton';
import {Box} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {IAnalyzerConfig} from '../../model';
import {AxiosError} from 'axios';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {FieldValues} from 'react-hook-form';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
type CustomIndexFormStep3Props = {
  setActiveStep: (value: number) => void;
  activeStep: number;
};

export interface IAnalyzerReqBody {
  name: string;
  stopwordList: [
    {
      _List: string;
    },
  ];
  caseConversionFlag: boolean;
  whitespaceCharList: string;
}

const CutomIndexStep3 = (props: CustomIndexFormStep3Props) => {
  const {setActiveStep, activeStep} = props;
  const {trackGAEvent} = useAnalytics();
  const {showToast} = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [analyzersList, setAnalyzersList] = useState<IAnalyzerConfig[]>([]);
  const [selectAnalyzer, setSelectAnalyzer] = useState<IAnalyzerConfig[]>([]);
  const [mode, setMode] = useState<string>('Edit');
  const [activeAnalyzer, setActiveAnalyzer] = useState<string>('');
  const [prevAnalyzer, setPrevAnalyzer] = useState<string>('');
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const {indexAnalyzers, selectedIndexAnalyzer} = useSelector(
    (state: RootState) => state.indexes
  );

  const fieldTemplateId = useSelector(
    (state: RootState) => state.indexes.createFieldTemplateId
  );
  const params = `?clientId=${clientId}&projectId=${projectId}`;
  const fetchIndex = async () => {
    if (clientId && projectId) {
      try {
        setReqLoading(true);
        const result = await getAnalyzers(params, fieldTemplateId);

        const results = result?.data?.data.map((data: IAnalyzerConfig) => {
          const analyzerName = data?.name;
          return {
            ...data,
            label: t(
              `customIndexAnalyzers.${analyzerName}.label`,
              analyzerName
            ),
            description: t(
              `customIndexAnalyzers.${analyzerName}.description`,
              ''
            ),
            type: 'Standard',
          };
        });

        const findVisibleItem = results.filter((item: any) => item.isVisible);
        if (
          !selectedIndexAnalyzer &&
          activeAnalyzer === '' &&
          findVisibleItem.length > 0
        ) {
          setActiveAnalyzer(findVisibleItem[0].label);
          setSelectAnalyzer([findVisibleItem[0]]);
        }

        dispatch(setIndexAnalyzers(results));
        setAnalyzersList(results);
      } catch (e) {
        console.error('Error', e);
        setReqLoading(false);
      } finally {
        setReqLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedIndexAnalyzer) {
      setSelectAnalyzer([selectedIndexAnalyzer]);
      setActiveAnalyzer(selectedIndexAnalyzer?.label ?? '');
    }
  }, [selectedIndexAnalyzer]);

  useEffect(() => {
    fetchIndex();
  }, [clientId, projectId]);

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.indexManagement.analyzerInformation'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  }, []);

  const selectedAnalyzer = (analyzer: string) => {
    if (mode === 'New') {
      const removeTemp = analyzersList.slice(0, analyzersList.length - 1);
      setAnalyzersList(removeTemp);
    }
    const filterAnalyzer = analyzersList.filter(
      (item: IAnalyzerConfig) => item.label === analyzer
    );
    setSelectAnalyzer(filterAnalyzer);
    setActiveAnalyzer(filterAnalyzer[0].label ?? '');
    setMode('Edit');
  };

  const handleDuplicateAnalyser = () => {
    setMode('New');
    setPrevAnalyzer(activeAnalyzer);
    setActiveAnalyzer('Custom Analyzer');
    const tempData = {
      ...selectAnalyzer[0],
      analyzer: 'Custom Analyzer',
      type: 'Custom',
    };
    setSelectAnalyzer([{...selectAnalyzer[0], name: '', type: 'Custom'}]);
    setAnalyzersList([...analyzersList, tempData]);
  };

  const handleAddAnalyzer = async (data: FieldValues) => {
    try {
      const payload = {
        name: data.customName,
        whitespaceCharList: data.whitespaceCharList,
        caseConversionFlag: data.caseConversionFlag,
        stopwordList: data.stopWords.map((word: string) => ({_List: word})),
      };
      const response = await createAnalyzer(params, payload);
      if (response?.data?.meta?.status === 'Success') {
        setActiveAnalyzer(data.customName);
        setSelectAnalyzer([{...payload, stopWordsList: data.stopWords}]);
        setMode('Edit');
        fetchIndex();
      }
    } catch (error: unknown) {
      const e = error as AxiosError;
      showToast(e.message, 'error', undefined, 'center');
    }
  };
  const handleCancel = () => {
    setCancelModal(true);
  };

  const navigateToUnsavedModal = () => {
    selectedAnalyzer(prevAnalyzer);
    setCancelModal(false);
    setMode('Edit');
    setAnalyzersList([...indexAnalyzers]);
  };

  const closeUnsavedModalHandler = () => {
    setCancelModal(false);
  };

  const OpenPopupHandler = () => {
    setConfirmModal(true);
  };

  const handleContinueOptionSelected = () => {
    setConfirmModal(false);
    navigate('/indexmanagement');
  };
  const closeModalHandler = () => {
    setConfirmModal(false);
  };

  return analyzersList.length != 0 ? (
    <>
      <div className="analyzers-split">
        <CustomIndexstep3LeftRail
          analyzersList={analyzersList}
          selectedAnalyzer={selectedAnalyzer}
          activeAnalyzer={activeAnalyzer}
        />
        <div className="analyzer-main">
          <AdjustAnalyzersContent
            selectedAnalyzer={selectAnalyzer[0]}
            mode={mode}
            handleDuplicateAnalyser={handleDuplicateAnalyser}
            handleAddAnalyzer={handleAddAnalyzer}
            handleCancel={handleCancel}
          />
        </div>
      </div>
      <Box className="index-action-container analyzer-action-footer">
        <div className="custom-index-action-button">
          <AurButton
            type="exit"
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            onClick={() => OpenPopupHandler()}
            dataTestId={'closeBtn-test'}
            classNames="index-cancel-btn"
            ariaDescription={t(
              'accessibility.indexManagement.create.cancelHint'
            )}
          />
          <AurButton
            type="continue"
            label={t('buttons.next')}
            title={t('buttons.next')}
            size="Medium"
            onClick={() => {
              dispatch(setSelectedIndexAnalyzer(selectAnalyzer[0]));

              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.indexManagement.next'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.TAB,
                  action: t(
                    'gaTracking.indexManagement.newIndexAnalyzerSettingReviewed'
                  ),
                },
                subPage: {
                  subpage_name: t(
                    'gaTracking.indexManagement.analyzerInformation'
                  ),
                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                },
              });
              setActiveStep(activeStep + 1);
            }}
            dataTestId={'closeBtn-test'}
            disabled={mode === 'New'}
            ariaDescription={t(
              'accessibility.indexManagement.create.continueHint'
            )}
          />
        </div>
      </Box>
      {cancelModal && (
        <AurPopup
          aria-label={t('analyzer.unsavedModalAriaLabel')}
          aria-description={t('analyzer.unsavedModalAriaDescription')}
          className="inactivity-popup-modal add-doc-unsaved-model"
          open={cancelModal}
          onOk={navigateToUnsavedModal}
          onCancel={closeUnsavedModalHandler}
          popupTitle={t(`common.cancelUploadPopup.title`)}
          popupHeading={t(`common.cancelUploadPopup.heading`)}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={false}
          popupContent={t(`common.cancelUploadPopup.content`)}
          labelActionCancel={t(`common.cancelUploadPopup.buttonStay`)}
          labelActionOk={t(`common.cancelUploadPopup.buttonYes`)}
        />
      )}
      {confirmModal && (
        <div>
          <AurPopup
            className="aur-unsaved-changes-popup add-doc-confirm-popup"
            open={confirmModal}
            onOk={handleContinueOptionSelected}
            onCancel={closeModalHandler}
            popupHeading={t('common.clearAllPopup.heading')}
            showPopupHeading={true}
            showPopupTitle={false}
            showCloseIcon={false}
            popupContent={t('indexes.customIndex.step1.confirmMessage')}
            labelActionCancel={t('common.clearAllPopup.buttonCancel')}
            labelActionOk={t('buttons.continue')}
            exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
            continueButtonHint={t('common.clearAllPopup.confirmToContinue')}
            cancelButtonTitle=""
          />
        </div>
      )}
    </>
  ) : (
    <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
  );
};

export default CutomIndexStep3;
